import * as React from 'react'

/**
 * Lets a transient overlay (Popover, DropdownMenu) know a Dialog was opened from
 * inside it, so it can drop below the modal layer while that dialog is up.
 *
 * Transient overlays sit *above* modals by default — see the layering note in
 * `dialog.tsx` — because a Select rendered inside a Dialog breaks otherwise. A Dialog
 * opened *from* an overlay is the one case where that ordering is backwards: the dialog
 * is the newer surface and belongs on top. React context crosses portals, so the dialog
 * finds the overlay it came from even though the two render into different DOM subtrees.
 */
export interface NestedDialogRegistry {
  onOpen: () => void
  onClose: () => void
}

export const NestedDialogContext = React.createContext<NestedDialogRegistry | null>(null)

/** Overlay side: tracks how many dialogs opened from inside this overlay are currently up. */
export function useNestedDialogRegistry() {
  const [openCount, setOpenCount] = React.useState(0)
  const registry = React.useMemo<NestedDialogRegistry>(
    () => ({
      onOpen: () => setOpenCount((count) => count + 1),
      onClose: () => setOpenCount((count) => Math.max(0, count - 1)),
    }),
    []
  )
  return { hasNestedDialog: openCount > 0, registry }
}

/**
 * Dialog side: registers with the enclosing overlay, if there is one. Dialog content is
 * mounted only while open, so mount/unmount tracks the dialog's open state exactly.
 */
export function useRegisterNestedDialog() {
  const registry = React.useContext(NestedDialogContext)
  React.useEffect(() => {
    if (!registry) return
    registry.onOpen()
    return registry.onClose
  }, [registry])
}
